import ReactPaginate from 'react-paginate';
import { Fragment, startTransition, useEffect, useState } from 'react';
import { usePersistedPage } from '../../hooks/common/usePersistedPage';
import { useWindowDimensions } from '../../hooks/common/useWindowDimensions';
import { PAGINATION } from '../../constants/common/pagination';
import { AdvertLoadersList } from '../common/loaders/AdvertLoader';
import { useLocation } from 'react-router-dom';
import { AdSlot } from '../common/ads';
import { getAdsenseSlot } from '../../services/adsense';
import VinAffiliateCta from '../checkauto/information/VinAffiliateCta';
import SocialFollowCard from './SocialFollowCard';

const IN_FEED_AFTER_INDEX = 2;
const FIRST_PAINT_CARDS = 3;
const VIN_CTA_AFTER_INDEX = 5;
const VEHICLE_SEARCH_RE = /\/search\/(full\/)?(car|bike|commercial)(\/|$)/;

const emitPaginationReset = (keys = []) => {
    if (typeof window === 'undefined') return;

    window.dispatchEvent(
        new CustomEvent('pagination:reset', {
            detail: { keys },
        }),
    );
};

export const clearAllPaginationStorage = () => {
    const keys = Object.values(PAGINATION);

    keys.forEach((key) => {
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`${key}_sort`);
    });

    emitPaginationReset(keys);
};

export const clearPaginationByCategory = (categories = []) => {
    const categoryKeys = {
        services: [
            PAGINATION.SERVICES_ALL_LIST,
            PAGINATION.SERVICES_CABINET_LIST,
            PAGINATION.TOP_SERVICES_LIST,
        ],
    };

    let keysToClear = [];
    categories.forEach((category) => {
        if (categoryKeys[category]) {
            keysToClear = [...keysToClear, ...categoryKeys[category]];
        }
    });

    keysToClear.forEach((key) => {
        sessionStorage.removeItem(key);
        sessionStorage.removeItem(`${key}_sort`);
    });

    emitPaginationReset(keysToClear);
};

export const clearServicesPagination = () => {
    clearPaginationByCategory(['services']);
};

export const Paginate = ({
    itemsPerPage,
    data,
    renderAdvert,
    storageKey,
    totalItems,
    currentPage,
    onPageChange,
    isLoading,
}) => {
    const { pageCount, handlePageClick } = usePersistedPage(
        storageKey,
        itemsPerPage,
        totalItems,
        currentPage,
        onPageChange,
    );

    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        startTransition(() => setShowAll(true));
    }, []);

    const items = Array.isArray(data) ? data : [];
    const visibleItems = showAll ? items : items.slice(0, FIRST_PAINT_CARDS);
    const reserveHeight = !showAll && items.length > FIRST_PAINT_CARDS;

    const { pathname } = useLocation();
    const isVehicleSearch = VEHICLE_SEARCH_RE.test(pathname);
    const showFollowCard =
        isMobile && isVehicleSearch && !isLoading && items.length > 0;

    const paginateLinkClassName = 'paginate__link';
    const paginateItemClassName = 'paginate__item';
    const activeLinkClassName = 'paginate__link--active';
    const disabledLinkClassName = 'paginate__link--disabled';

    return (
        <>
            <div style={reserveHeight ? { minHeight: '100vh' } : undefined}>
                {isLoading ? (
                    <AdvertLoadersList />
                ) : (
                    visibleItems.map((item, index) => {
                        const card = renderAdvert(item, index);
                        if (index === IN_FEED_AFTER_INDEX) {
                            return (
                                <Fragment key={`${item.id || index}-with-ad`}>
                                    {card}
                                    <AdSlot
                                        slotId={getAdsenseSlot('searchInFeed')}
                                        format="fluid"
                                        layoutKey={
                                            getAdsenseSlot(
                                                'searchInFeedLayoutKey',
                                            ) || '-fb+5w+4e-db+86'
                                        }
                                        style={{ margin: '1rem 0' }}
                                    />
                                </Fragment>
                            );
                        }
                        if (isVehicleSearch && index === VIN_CTA_AFTER_INDEX) {
                            return (
                                <Fragment key={`${item.id || index}-with-vin`}>
                                    {card}
                                    <VinAffiliateCta placement="search_feed" />
                                </Fragment>
                            );
                        }
                        return card;
                    })
                )}
            </div>
            {showFollowCard && <SocialFollowCard />}
            <ReactPaginate
                onPageChange={handlePageClick}
                pageRangeDisplayed={isMobile ? 1 : 3}
                marginPagesDisplayed={isMobile ? 1 : 2}
                pageCount={pageCount > 0 ? pageCount : 1}
                forcePage={
                    pageCount > 0 ? Math.min(currentPage, pageCount - 1) : 0
                }
                pageClassName={paginateItemClassName}
                pageLinkClassName={paginateLinkClassName}
                previousClassName={paginateItemClassName}
                previousLinkClassName={paginateLinkClassName}
                previousLabel="<"
                nextClassName={paginateItemClassName}
                nextLinkClassName={paginateLinkClassName}
                activeLinkClassName={activeLinkClassName}
                disabledLinkClassName={disabledLinkClassName}
                nextLabel=">"
                breakLabel="..."
                breakClassName={paginateItemClassName}
                breakLinkClassName={paginateLinkClassName}
                containerClassName={
                    totalItems > itemsPerPage ? 'pagination' : 'no-pagination'
                }
                activeClassName="active"
                renderOnZeroPageCount={null}
            />
        </>
    );
};
